import { makeDiscriminatedUnionPredicate } from '@sgme/fp';
import type { AddonFormsRequest } from 'apiConnector/addon-management';
import { addonManagementApiConnector } from 'apiConnector/addon-management';
import { isNavigatingInAddonManagement } from 'epic/utils/navigation/navigation';
import type { AddonManagementFormResult } from 'models/forms/AddonManagementFormResult';
import type { Observable } from 'rxjs';
import { of } from 'rxjs';
import { catchError, filter, mergeMap, skipUntil, switchMap, withLatestFrom } from 'rxjs/operators';
import type { Action } from 'state/actions';
import { addonFormsFailed, addonFormsReceived } from 'state/addonManagementForm/addonManagementForm.action';
import { showNotificationThunk } from 'state/notifications/notification.thunk';
import type { EpicWithThunk } from 'state/state.common';

export interface FetchAddonManagementFormsEpicApi {
  initForm(request: AddonFormsRequest): Observable<AddonManagementFormResult>;
}

const isFetchingAction = makeDiscriminatedUnionPredicate('type', [
  'ADDON_MANAGEMENT_FILTER_CHANGED',
  'ADDON_MANAGEMENT_FILTERS_LOADED',
  'ADDON_MANAGEMENT_FORMS_REQUESTED',
] satisfies Action['type'][]);

export function fetchAddonManagementFormsEpic(): EpicWithThunk {
  return (action$, state$, { selectors: { getAddonManagementFormsRequest }, httpClient }) => {
    const navigateIn$ = action$.pipe(filter(isNavigatingInAddonManagement));
    const filtersLoaded$ = action$.pipe(
      filter(
        ({ type }) => type === 'ADDON_MANAGEMENT_FILTERS_LOADED' || type === 'ADDON_MANAGEMENT_FILTERS_LOADING_FAILED',
      ),
    );

    return action$
      .pipe(filter(isFetchingAction), skipUntil(filtersLoaded$), skipUntil(navigateIn$), withLatestFrom(state$))
      .pipe(
        withLatestFrom(state$),
        switchMap(([, state]) => {
          const requestData = getAddonManagementFormsRequest(state);
          return addonManagementApiConnector(httpClient)
            .initForms(requestData)
            .pipe(
              mergeMap(({ forms, isTradesFetchSuccessful }) => of(addonFormsReceived(forms, isTradesFetchSuccessful))),
              catchError((_: Error) => {
                return of(
                  addonFormsFailed(new Error('Addon management forms failed')),
                  showNotificationThunk({
                    level: 'danger',
                    title: { id: 'toast.unexpectedError' },
                    content: [{ id: 'pleaseContactSupport' }],
                  }),
                );
              }),
            );
        }),
      );
  };
}
